import { useEffect, useImperativeHandle, useState } from "react";

interface FlippableCardProps {
  frontContent: React.ReactNode;
  backContent: React.ReactNode;
  frontFlipButtonText?: string;
  backFlipButtonText?: string;
  backActionButton?: React.ReactNode;
}

export const FlippableCard: React.FC<FlippableCardProps> = ({
  frontContent,
  backContent,
  frontFlipButtonText = "Flip",
  backFlipButtonText = "Back to question",
  backActionButton,
}) => {
  const [isFlipped, setIsFlipped] = useState(false);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === "Space") {
        e.preventDefault();
        setIsFlipped((f) => !f);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div
      className="relative w-96 sm:min-w-xl sm:w-[576px] h-96 cursor-pointer"
      style={{ perspective: "1500px" }}
      onClick={() => setIsFlipped(!isFlipped)}
    >
      <div
        className="relative w-full h-full transition-transform duration-700"
        style={{
          transformStyle: "preserve-3d",
          transform: isFlipped ? "rotateY(-180deg)" : "rotateY(0deg)",
          transitionTimingFunction: "cubic-bezier(0.4, 0.0, 0.2, 1)",
        }}
      >
        {/* Front */}
        <div
          className="absolute inset-0 flex flex-col justify-between items-start p-8 rounded-4xl border border-theme bg-theme-0 shadow-md"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
          }}
        >
          <div className="w-full">{frontContent}</div>
          <button className="text-theme-1 font-sans text-lg font-medium leading-none -m-2 p-2">
            {frontFlipButtonText}
          </button>
        </div>

        {/* Back */}
        <div
          className="absolute inset-0 flex flex-col justify-between items-start p-8 rounded-4xl border border-theme bg-theme-0 shadow-md"
          style={{
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            transform: "rotateY(-180deg)",
          }}
        >
          <div className="w-full flex-1 min-h-0">{backContent}</div>
          <div className="flex justify-between items-end w-full -m-2">
            <button className="text-theme-1 font-sans text-lg font-medium leading-none p-2">
              {backFlipButtonText}
            </button>
            <div className="-mr-3">{backActionButton && backActionButton}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
